
/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import React from "react";
import { formatDigits } from "../../Utils/formatNumbers";
import { dateStringTimeConvert } from "../../Utils/titleCaseFunction";
import compayInfo from '../../Helpers/companyAddress.json';
import { useCompany } from "../../Context/company-context";

// ====== STYLES ======
const container = css`
  width: 100%;
  margin: auto;
  padding: 15px;
  font-family: Arial, sans-serif;
  color: #333;
  background-color: #fff;
  box-sizing: border-box;

  @media print {
    @page {
      size: A4 portrait;
      margin: 15mm 15mm 15mm 15mm;
    }
    width: 100%;
    min-height: 297mm;
    padding: 5mm;
    -webkit-print-color-adjust: exact;
  }

  h1 {
    font-size: 16pt;
  }

  h2 {
    font-size: 15pt;
  }

  td,
  th {
    font-size: 12pt;
  }
`;

const header = css`
  text-align: center;
  margin-bottom: 20px;

  img {
    display: block;
    margin: 0 auto 10px;
    max-width: 150px;
  }

  h1 {
    margin: 0;
    font-size: 15pt;
    font-weight: bold;
  }

  h2 {
    margin: 5px 0 15px;
    font-size: 14pt;
    font-weight: bold;
  }

  p {
    margin: 2px 0;
    font-size: 12pt;
  }
`;

const customerTable = css`
  width: 100%;
  margin-bottom: 20px;
  border-collapse: collapse;
  font-size: 10pt;

  td {
    padding: 4px 6px;
    vertical-align: top;
    color: #333;
    font-size: 14pt;
  }

  strong {
    color: #555;
  }

  tr + tr td {
    padding-top: 2px;
  }
`;

const productsTable = css`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;

  th,
  td {
    border: 1px solid #ccc;
    padding: 1px;
    font-size: 15pt;
    color: #000;
  }

  th {
    background-color: #f2f2f2;
    font-weight: bold;
    text-align: left;
    color: #000;
    font-size: 16pt;
  }

  td {
    text-align: left;
  }

  td.amount,
  td.unitPrice {
    text-align: right;
  }
`;

const totalsTable = css`
  width: 100%;
  border-collapse: collapse;
  margin-top: 5px;
  font-size: 14pt;

  td {
    padding: 8px;
    color: #000;
    font-weight: bold;
    font-size: 14pt;
  }

  td.right {
    text-align: right;
  }
`;

const footer = css`
  margin-top: 40px;
  font-size: 12pt;
  text-align: center;
  border-top: 1px solid #ccc;
  padding-top: 10px;
`;

// ====== COMPONENT ======
const A4Receipt = React.forwardRef(({customer, products }, ref) => {
  const user_name = `${customer.first_name || ""}`.trim();
  const { state:{ companyName, companyEmail, mobile, address, website, logo } } = useCompany();

  return (
    <div css={container} ref={ref} id="a4Print">
      {/* Header */}
      <div css={header}>
        {logo && <img src={logo} alt={companyName} />}
        <h1>{companyName || "VIP PHARMACY"}</h1>
        <p>{address || "Address not available"}</p>
        <p>Phone: {mobile || "N/A"}</p>
        <p>Email: {companyEmail || "N/A"}</p>
        <h2>Sales Receipt</h2>
      </div>

      {/* Customer Info */}
      <table css={customerTable}>
        <tbody>
          <tr>
            <td>
              <strong>Received From:</strong> {customer.customer_name || ""}
            </td>
            <td>
              <strong>Date:</strong> {customer.date ? dateStringTimeConvert(customer.date) : ""}
            </td>
          </tr>
          <tr>
            <td>
              <strong>Receipt#:</strong> {customer.receipt_number || ""}
            </td>
          </tr>
          <tr>
            <td>
              <strong>Address:</strong> {customer.customer_address || ""}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Products Table */}
      <table css={productsTable}>
        <thead>
          <tr>
            <th>No.</th>
            <th>Brand Name</th>
            <th>Batch</th>
            <th>Expiry</th>
            <th>Qty</th>
            <th>Unit Price</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => {
            const qty = p.quantity ? +p.quantity : 0;
            const rate = p.rate ? +p.rate : 0;
            const discount = p.discount_amount ? +p.discount_amount : 0;

            const final_rate = rate - discount;
            const total = final_rate * qty;

            return (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{p.product_name}</td>
                <td>{p.batch_id}</td>
                <td>{p.expire_date}</td>
                <td>{qty}</td>
                <td className="unitPrice">{formatDigits(final_rate)}</td>
                <td className="amount">{formatDigits(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Totals Section */}
      <table css={totalsTable}>
        <tbody>
          <tr>
            <td>Total Amount:</td>
            <td className="right">{formatDigits(customer.total_amount)}</td>
          </tr>
          <tr>
            <td>Amount Received:</td>
            <td className="right">
              {formatDigits((customer.amount_paid || 0) + (customer.balance || 0))}
            </td>
          </tr>
          <tr>
            <td>Change:</td>
            <td className="right">{formatDigits(customer.balance || 0)}</td>
          </tr>
          {/* <tr>
            <td>Total Discount:</td>
            <td className="right">{formatDigits(customer.total_discount || 0)}</td>
          </tr> */}
        </tbody>
      </table>

      {/* Footer */}
      <div css={footer}>
        Received By: {user_name || "System"}
        {/* <p>     Merry Christmas & Happy Holidays!    </p> */}
 
        </div>
    </div>
  );
});

export default A4Receipt;
